import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import dayjs from "dayjs";
import { useGlobal } from "../contexts/GlobalContext";
import { useCurrentProject } from "../hooks/useCurrentProject";
import { useTranslation } from "../i18n";
import request from "../utils/request";
import AnalyticsMetricGrid from "../components/analytics/AnalyticsMetricGrid";
import AnalyticsSetupGuide from "../components/analytics/AnalyticsSetupGuide";
import PlanGate from "../componets/common/PlanGate";
import WidePageHint from "../componets/common/WidePageHint";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const { Paragraph, Text } = Typography;

interface AnalyticsSite {
  id: number;
  site_name: string;
  domain: string;
  site_key: string;
  status: boolean;
  settings?: Record<string, any>;
}

interface SummaryStats {
  pageviews: number;
  visitors: number;
  sessions: number;
  ips: number;
  bounceRate: number;
  avgDurationMs: number;
  newVisitors: number;
  returnVisitors: number;
}

interface TrendPoint {
  bucket: string;
  pageviews: number;
  visitors: number;
  sessions: number;
}

interface DimensionStat {
  key: string;
  pageviews: number;
  visitors: number;
}

interface SourceStats {
  byType: { type: string; pageviews: number; visitors: number }[];
  topDomains: DimensionStat[];
  searchKeywords: DimensionStat[];
}

interface PageStats {
  topPages: DimensionStat[];
  entryPages: DimensionStat[];
  exitPages: DimensionStat[];
}

interface RealtimeStats {
  active5Min: number;
  active30Min: number;
  recentVisits: {
    visitorId: string;
    country: string;
    city: string;
    browser: string;
    os: string;
    deviceType: string;
    url: string;
    title: string;
    lastSeen: string;
  }[];
}

interface AnalyticsGoal {
  id: number;
  name: string;
  goalType?: string;
  goal_type?: string;
  matchMode?: string;
  match_mode?: string;
  matchValue?: string;
  match_value?: string;
  stepOrder?: number;
  step_order?: number;
  funnelId?: string;
  funnel_id?: string;
  status?: boolean;
}

interface GoalConversion {
  goalId?: number;
  goal_id?: number;
  name?: string;
  count?: number;
  visitors?: number;
  conversionRate?: number;
  conversion_rate?: number;
}

interface FunnelStep {
  goalId?: number;
  goal_id?: number;
  name?: string;
  stepOrder?: number;
  step_order?: number;
  count?: number;
  visitors?: number;
  conversionRate?: number;
  conversion_rate?: number;
}

// AI 报告(后端 /api/v3/analytics/ai-report -> service.GenerateAIReport)
interface AIReportResult {
  siteId: number;
  from: string;
  to: string;
  summary?: string[];
  alerts?: string[];
  suggestions?: string[];
}

const RANGE_OPTIONS = [
  { label: "近 24 小时", value: 1 },
  { label: "近 7 天", value: 7 },
  { label: "近 30 天", value: 30 },
  { label: "近 90 天", value: 90 },
];

const dimensionColumns = (keyTitle: string) => [
  { title: keyTitle, dataIndex: "key", key: "key", ellipsis: true },
  { title: "浏览量", dataIndex: "pageviews", key: "pageviews", width: 100 },
  { title: "访客数", dataIndex: "visitors", key: "visitors", width: 100 },
];

// 站点分析(PRD v4 10§4.2 / TODOLIST R1-H):站点列表/创建、安装指引、概览、趋势、来源、页面、实时
const Analytics: React.FC = () => {
  const { API_BASE_URL } = useGlobal();
  const { project: routeProject } = useCurrentProject();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sites, setSites] = useState<AnalyticsSite[]>([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [siteId, setSiteId] = useState<number | null>(null);
  const [rangeDays, setRangeDays] = useState(7);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm] = Form.useForm();

  const [summary, setSummary] = useState<SummaryStats | null>(null);
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const [sources, setSources] = useState<SourceStats | null>(null);
  const [pages, setPages] = useState<PageStats | null>(null);
  const [realtime, setRealtime] = useState<RealtimeStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [goals, setGoals] = useState<AnalyticsGoal[]>([]);
  const [conversions, setConversions] = useState<GoalConversion[]>([]);
  const [funnel, setFunnel] = useState<FunnelStep[]>([]);
  const [funnelId, setFunnelId] = useState<string>();
  const [goalsLoading, setGoalsLoading] = useState(false);
  const [goalCreating, setGoalCreating] = useState(false);
  const [goalForm] = Form.useForm();
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsForm] = Form.useForm();
  const [exportLoading, setExportLoading] = useState(false);
  const [aiReport, setAiReport] = useState<AIReportResult | null>(null);
  const [aiReportLoading, setAiReportLoading] = useState(false);

  const currentSite = useMemo(() => sites.find((s) => s.id === siteId) || null, [sites, siteId]);

  const fetchSites = useCallback(async () => {
    setSitesLoading(true);
    try {
      const res = await request.get(`${API_BASE_URL}/api/v3/analytics/sites`);
      const list: AnalyticsSite[] = res.data?.data || [];
      setSites(list);
      setSiteId((prev) => (prev && list.some((s) => s.id === prev) ? prev : list[0]?.id ?? null));
    } catch (e: any) {
      message.error(e?.response?.data?.msg || "获取站点列表失败");
    } finally {
      setSitesLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  // 项目作用域：自动选中 site_key 匹配当前项目的站点（统计站点跟随项目）
  useEffect(() => {
    if (!routeProject?.key_value || sites.length === 0) return;
    const match = sites.find((s) => s.site_key === routeProject.key_value);
    if (match) setSiteId(match.id);
  }, [routeProject?.key_value, sites]);

  const rangeParams = useMemo(() => {
    const to = dayjs();
    const from = to.subtract(rangeDays, "day");
    return { from: from.toISOString(), to: to.toISOString() };
  }, [rangeDays]);

  const fetchStats = useCallback(async () => {
    if (!siteId) return;
    setStatsLoading(true);
    const base = `${API_BASE_URL}/api/v3/analytics`;
    const params = { siteId, ...rangeParams } as any;
    const granularity = rangeDays <= 1 ? "hour" : "day";
    try {
      const [summaryRes, trendRes, sourcesRes, pagesRes, realtimeRes] = await Promise.all([
        request.get(`${base}/summary`, { params }),
        request.get(`${base}/trend`, { params: { ...params, granularity } }),
        request.get(`${base}/sources`, { params }),
        request.get(`${base}/pages`, { params }),
        request.get(`${base}/realtime`, { params: { siteId } }),
      ]);
      setSummary(summaryRes.data?.data || null);
      setTrend(trendRes.data?.data || []);
      setSources(sourcesRes.data?.data || null);
      setPages(pagesRes.data?.data || null);
      setRealtime(realtimeRes.data?.data || null);
    } catch (e: any) {
      message.error(e?.response?.data?.msg || "获取统计数据失败");
    } finally {
      setStatsLoading(false);
    }
  }, [API_BASE_URL, siteId, rangeParams, rangeDays]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const fetchGoals = useCallback(async () => {
    if (!siteId) return;
    setGoalsLoading(true);
    const base = `${API_BASE_URL}/api/v3/analytics`;
    const params = { siteId, ...rangeParams };
    try {
      const [goalRes, conversionRes] = await Promise.all([
        request.get(`${base}/goals`, { params: { siteId } }),
        request.get(`${base}/conversions`, { params }),
      ]);
      const nextGoals: AnalyticsGoal[] = goalRes.data?.data || [];
      setGoals(nextGoals);
      setConversions(conversionRes.data?.data || []);
      const nextFunnel = funnelId || nextGoals.find((goal) => goal.funnelId || goal.funnel_id)?.funnelId || nextGoals.find((goal) => goal.funnel_id)?.funnel_id;
      if (nextFunnel) {
        setFunnelId(nextFunnel);
        const funnelRes = await request.get(`${base}/funnel`, { params: { ...params, funnelId: nextFunnel } });
        setFunnel(funnelRes.data?.data || []);
      } else {
        setFunnel([]);
      }
    } catch (e: any) {
      message.error(e?.response?.data?.msg || "加载目标失败");
    } finally {
      setGoalsLoading(false);
    }
  }, [API_BASE_URL, siteId, rangeParams, funnelId]);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  const fetchSettings = useCallback(async () => {
    if (!siteId) return;
    setSettingsLoading(true);
    try {
      const res = await request.get(`${API_BASE_URL}/api/v3/analytics/settings`, { params: { siteId } });
      settingsForm.setFieldsValue(res.data?.data || {});
    } catch (e: any) {
      message.error(e?.response?.data?.msg || "加载设置失败");
    } finally {
      setSettingsLoading(false);
    }
  }, [API_BASE_URL, siteId, settingsForm]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSaveSettings = async () => {
    if (!siteId) return;
    const values = await settingsForm.validateFields();
    setSettingsSaving(true);
    try {
      await request.post(`${API_BASE_URL}/api/v3/analytics/settings`, {
        siteId,
        excludeIps: values.excludeIps || [],
        excludePathPrefix: values.excludePathPrefix || [],
        excludeUas: values.excludeUas || [],
        excludeVisitorIds: values.excludeVisitorIds || [],
        anonymizeIp: Boolean(values.anonymizeIp),
      });
      message.success("设置已保存");
      fetchSites();
    } catch (e: any) {
      message.error(e?.response?.data?.msg || "保存设置失败");
    } finally {
      setSettingsSaving(false);
    }
  };

  // AI 报告:调用后端真实端点 /api/v3/analytics/ai-report(service.GenerateAIReport),
  // 返回 summary/alerts/suggestions 三类要点;后端按套餐校验,403 时由 PlanGate 兜底提示。
  const fetchAIReport = async () => {
    if (!siteId) return;
    setAiReportLoading(true);
    try {
      const res = await request.get(`${API_BASE_URL}/api/v3/analytics/ai-report`, {
        params: { siteId, ...rangeParams },
      });
      setAiReport(res.data?.data || null);
    } catch (e: any) {
      message.error(e?.response?.data?.msg || t("analytics.insights.loadFailed"));
    } finally {
      setAiReportLoading(false);
    }
  };

  const exportCSV = async () => {
    if (!siteId) return;
    setExportLoading(true);
    try {
      const res = await request.get(`${API_BASE_URL}/api/v3/analytics/export.csv`, {
        params: { siteId, ...rangeParams },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `analytics-${siteId}-${dayjs().format("YYYYMMDDHHmmss")}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      message.error(e?.response?.data?.msg || "Export CSV failed");
    } finally {
      setExportLoading(false);
    }
  };

  const handleCreateGoal = async () => {
    if (!siteId) return;
    const values = await goalForm.validateFields();
    setGoalCreating(true);
    try {
      await request.post(`${API_BASE_URL}/api/v3/analytics/goals`, {
        siteId,
        name: values.name,
        goalType: values.goalType,
        matchMode: values.matchMode,
        matchValue: values.matchValue,
        stepOrder: Number(values.stepOrder || 1),
        funnelId: values.funnelId || "",
        status: true,
      });
      goalForm.resetFields();
      message.success("目标已创建");
      fetchGoals();
    } catch (e: any) {
      message.error(e?.response?.data?.msg || "创建目标失败");
    } finally {
      setGoalCreating(false);
    }
  };

  const handleDeleteGoal = (goal: AnalyticsGoal) => {
    if (!siteId) return;
    Modal.confirm({
      title: "删除目标",
      content: goal.name,
      okText: "删除",
      cancelText: "取消",
      onOk: async () => {
        await request.post(`${API_BASE_URL}/api/v3/analytics/goals/delete`, { siteId, id: goal.id });
        message.success("目标已删除");
        fetchGoals();
      },
    });
  };

  const handleToggleGoal = async (goal: AnalyticsGoal) => {
    if (!siteId) return;
    const nextStatus = goal.status === false;
    try {
      await request.post(`${API_BASE_URL}/api/v3/analytics/goals/update`, { siteId, id: goal.id, status: nextStatus });
      message.success(nextStatus ? "目标已启用" : "目标已停用");
      fetchGoals();
    } catch (e: any) {
      message.error(e?.response?.data?.msg || "更新目标失败");
    }
  };

  const handleCreate = async () => {
    const values = await createForm.validateFields();
    setCreating(true);
    try {
      await request.post(`${API_BASE_URL}/api/v3/analytics/sites`, {
        siteName: values.siteName,
        domain: values.domain,
      });
      message.success("站点已创建,请按指引安装统计代码");
      setCreateOpen(false);
      createForm.resetFields();
      fetchSites();
    } catch (e: any) {
      message.error(e?.response?.data?.msg || "创建失败");
    } finally {
      setCreating(false);
    }
  };

  const installSnippet = currentSite
    ? `<script async src="${API_BASE_URL}/api/analytics/sdk.js" data-site-key="${currentSite.site_key}"></script>`
    : "";

  const hasData = (summary?.pageviews ?? 0) > 0;
  const isInstalled = hasData || currentSite?.settings?.analytics_installed;

  const trendChartData = useMemo(
    () => ({
      labels: trend.map((p) => dayjs(p.bucket).format(rangeDays <= 1 ? "HH:mm" : "MM-DD")),
      datasets: [
        {
          label: "浏览量(PV)",
          data: trend.map((p) => p.pageviews),
          borderColor: "#1677ff",
          backgroundColor: "rgba(22,119,255,0.12)",
          fill: true,
          tension: 0.3,
        },
        {
          label: "访客数(UV)",
          data: trend.map((p) => p.visitors),
          borderColor: "#52c41a",
          backgroundColor: "rgba(82,196,26,0.10)",
          fill: true,
          tension: 0.3,
        },
      ],
    }),
    [trend, rangeDays]
  );

  const funnelOptions = useMemo(() => {
    const ids = Array.from(new Set(goals.map((goal) => goal.funnelId || goal.funnel_id).filter(Boolean) as string[]));
    return ids.map((id) => ({ label: id, value: id }));
  }, [goals]);

  const goalColumns = [
    { title: "名称", dataIndex: "name", key: "name", ellipsis: true },
    { title: "类型", key: "type", width: 120, render: (_: any, r: AnalyticsGoal) => r.goalType || r.goal_type || "-" },
    { title: "匹配", key: "match", ellipsis: true, render: (_: any, r: AnalyticsGoal) => `${r.matchMode || r.match_mode || "-"} ${r.matchValue || r.match_value || ""}` },
    { title: "漏斗", key: "funnel", width: 140, render: (_: any, r: AnalyticsGoal) => r.funnelId || r.funnel_id || "-" },
    { title: "步骤", key: "step", width: 80, render: (_: any, r: AnalyticsGoal) => r.stepOrder || r.step_order || "-" },
    { title: "状态", key: "status", width: 90, render: (_: any, r: AnalyticsGoal) => <Tag color={r.status === false ? "default" : "green"}>{r.status === false ? "停用" : "启用"}</Tag> },
    {
      title: "操作",
      key: "action",
      width: 150,
      render: (_: any, r: AnalyticsGoal) => (
        <Space size={4}>
          <Button type="link" onClick={() => handleToggleGoal(r)}>{r.status === false ? "启用" : "停用"}</Button>
          <Button danger type="link" onClick={() => handleDeleteGoal(r)}>删除</Button>
        </Space>
      ),
    },
  ];

  const conversionColumns = [
    { title: "目标", dataIndex: "name", key: "name", ellipsis: true },
    { title: "次数", dataIndex: "count", key: "count", width: 90 },
    { title: "访客", dataIndex: "visitors", key: "visitors", width: 90 },
    {
      title: "转化率",
      key: "rate",
      width: 90,
      render: (_: any, r: GoalConversion) => `${(((r.conversionRate ?? r.conversion_rate ?? 0) as number) * 100).toFixed(1)}%`,
    },
  ];

  const funnelColumns = [
    { title: "步骤", key: "step", width: 80, render: (_: any, r: FunnelStep) => r.stepOrder || r.step_order || "-" },
    { title: "目标", dataIndex: "name", key: "name", ellipsis: true },
    { title: "访客", dataIndex: "visitors", key: "visitors", width: 90 },
    {
      title: "转化率",
      key: "rate",
      width: 90,
      render: (_: any, r: FunnelStep) => `${(((r.conversionRate ?? r.conversion_rate ?? 0) as number) * 100).toFixed(1)}%`,
    },
  ];

  if (!sitesLoading && sites.length === 0) {
    return (
      <Card title="站点分析">
        <AnalyticsSetupGuide mode="empty" onCreate={() => setCreateOpen(true)} />
        <CreateSiteModal
          open={createOpen}
          confirmLoading={creating}
          form={createForm}
          onOk={handleCreate}
          onCancel={() => setCreateOpen(false)}
        />
      </Card>
    );
  }

  return (
    <div>
      <WidePageHint message="站点分析含多列图表与表格,建议在桌面端查看。" />
      <Card
        title="站点分析"
        extra={
          <Space wrap>
            <Tag color="blue" style={{ height: 32, lineHeight: "30px", padding: "0 12px" }}>
              {currentSite ? `${currentSite.site_name} (${currentSite.domain})` : (sitesLoading ? "加载中…" : "未接入统计")}
            </Tag>
            <Select style={{ width: 130 }} value={rangeDays} onChange={setRangeDays} options={RANGE_OPTIONS} />
            <Button onClick={() => setCreateOpen(true)}>新建站点</Button>
            <Button onClick={fetchStats} loading={statsLoading}>
              刷新
            </Button>
            <Button onClick={exportCSV} loading={exportLoading} disabled={!siteId}>
              CSV
            </Button>
            {/* 效果 -> 原因 -> 成本 三者互跳(11§6.6) */}
            <Button onClick={() => navigate("/agents")}>Agent 运行</Button>
            <Button onClick={() => navigate("/token-usage")}>消耗记录</Button>
          </Space>
        }
      >
        {currentSite && !isInstalled && !statsLoading && (
          <AnalyticsSetupGuide mode="waiting" installSnippet={installSnippet} />
        )}

        <div style={{ marginBottom: 16 }}>
          <AnalyticsMetricGrid
            loading={statsLoading}
            metrics={[
              { key: "pv", title: "浏览量(PV)", value: summary?.pageviews ?? 0 },
              { key: "uv", title: "独立访客(UV)", value: summary?.visitors ?? 0 },
              { key: "bounce", title: "跳出率", value: ((summary?.bounceRate ?? 0) * 100).toFixed(1), suffix: "%" },
              { key: "duration", title: "平均停留", value: Math.round((summary?.avgDurationMs ?? 0) / 1000), suffix: "秒" },
            ]}
          />
        </div>

        <Tabs
          items={[
            {
              key: "trend",
              label: "趋势",
              children: (
                <div style={{ maxWidth: "100%", minHeight: 280 }}>
                  {trend.length > 0 ? (
                    <Line
                      data={trendChartData}
                      options={{ responsive: true, plugins: { legend: { position: "top" as const } } }}
                    />
                  ) : (
                    <Empty description="该区间暂无数据" />
                  )}
                </div>
              ),
            },
            {
              key: "sources",
              label: "来源",
              children: (
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <Card size="small" title="来源类型">
                      {(sources?.byType || []).map((t) => (
                        <div key={t.type} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                          <Tag>{t.type}</Tag>
                          <span>
                            PV {t.pageviews} / UV {t.visitors}
                          </span>
                        </div>
                      ))}
                      {(sources?.byType || []).length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                    </Card>
                  </Col>
                  <Col xs={24} md={8}>
                    <Card size="small" title="Top 来源域名">
                      <Table
                        size="small"
                        rowKey="key"
                        loading={statsLoading}
                        dataSource={sources?.topDomains || []}
                        columns={dimensionColumns("域名")}
                        pagination={false}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} md={8}>
                    <Card size="small" title="搜索关键词">
                      <Table
                        size="small"
                        rowKey="key"
                        loading={statsLoading}
                        dataSource={sources?.searchKeywords || []}
                        columns={dimensionColumns("关键词")}
                        pagination={false}
                      />
                    </Card>
                  </Col>
                </Row>
              ),
            },
            {
              key: "pages",
              label: "页面",
              children: (
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <Card size="small" title="热门页面">
                      <Table size="small" rowKey="key" loading={statsLoading} dataSource={pages?.topPages || []} columns={dimensionColumns("页面")} pagination={false} />
                    </Card>
                  </Col>
                  <Col xs={24} md={8}>
                    <Card size="small" title="入口页面">
                      <Table size="small" rowKey="key" loading={statsLoading} dataSource={pages?.entryPages || []} columns={dimensionColumns("页面")} pagination={false} />
                    </Card>
                  </Col>
                  <Col xs={24} md={8}>
                    <Card size="small" title="退出页面">
                      <Table size="small" rowKey="key" loading={statsLoading} dataSource={pages?.exitPages || []} columns={dimensionColumns("页面")} pagination={false} />
                    </Card>
                  </Col>
                </Row>
              ),
            },
            {
              key: "realtime",
              label: "实时",
              children: (
                <div>
                  <Space size="large" style={{ marginBottom: 16 }}>
                    <Statistic title="5 分钟活跃" value={realtime?.active5Min ?? 0} />
                    <Statistic title="30 分钟活跃" value={realtime?.active30Min ?? 0} />
                  </Space>
                  <Table
                    size="small"
                    rowKey={(r) => `${r.visitorId}-${r.lastSeen}`}
                    loading={statsLoading}
                    dataSource={realtime?.recentVisits || []}
                    pagination={false}
                    columns={[
                      { title: "页面", dataIndex: "title", key: "title", ellipsis: true, render: (v, r) => v || r.url },
                      { title: "位置", key: "loc", width: 140, render: (_, r) => [r.country, r.city].filter(Boolean).join(" / ") || "-" },
                      { title: "终端", key: "device", width: 180, render: (_, r) => `${r.browser || "-"} / ${r.os || "-"}` },
                      { title: "最近活跃", dataIndex: "lastSeen", key: "lastSeen", width: 160, render: (v) => dayjs(v).format("MM-DD HH:mm:ss") },
                    ]}
                  />
                </div>
              ),
            },
            {
              key: "goals",
              label: t("analytics.tab.goals"),
              children: (
                <PlanGate feature={t("analytics.gate.goals")} requiredPlan="pro" description={t("analytics.gate.goalsDesc")}>
                <Space direction="vertical" style={{ width: "100%" }} size="middle">
                    <Card size="small" title="新建目标">
                      <Form form={goalForm} layout="inline" initialValues={{ goalType: "pageview", matchMode: "contains", stepOrder: 1 }}>
                        <Form.Item name="name" rules={[{ required: true, message: "请输入目标名称" }]}>
                          <Input placeholder="目标名称" style={{ width: 180 }} />
                        </Form.Item>
                        <Form.Item name="goalType">
                          <Select style={{ width: 120 }} options={[{ value: "pageview", label: "页面浏览" }, { value: "event", label: "事件" }, { value: "duration", label: "停留时长" }]} />
                        </Form.Item>
                        <Form.Item name="matchMode">
                          <Select style={{ width: 120 }} options={[{ value: "contains", label: "包含" }, { value: "equal", label: "等于" }, { value: "prefix", label: "前缀" }, { value: "regex", label: "正则" }]} />
                        </Form.Item>
                        <Form.Item name="matchValue" rules={[{ required: true, message: "请输入匹配值" }]}>
                          <Input placeholder="如 /pricing 或 signup" style={{ width: 200 }} />
                        </Form.Item>
                        <Form.Item name="funnelId">
                          <Input placeholder="漏斗 ID" style={{ width: 130 }} />
                        </Form.Item>
                        <Form.Item name="stepOrder">
                          <Input type="number" min={1} style={{ width: 90 }} />
                        </Form.Item>
                        <Button type="primary" loading={goalCreating} onClick={handleCreateGoal}>
                          创建
                        </Button>
                      </Form>
                    </Card>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} lg={14}>
                        <Card size="small" title="转化目标">
                          <Table size="small" rowKey="id" loading={goalsLoading} dataSource={goals} columns={goalColumns} pagination={{ pageSize: 8 }} />
                        </Card>
                      </Col>
                      <Col xs={24} lg={10}>
                        <Card size="small" title="转化情况">
                          <Table size="small" rowKey={(r) => String(r.goalId || r.goal_id || r.name)} loading={goalsLoading} dataSource={conversions} columns={conversionColumns} pagination={false} />
                        </Card>
                      </Col>
                    </Row>
                    <Card
                      size="small"
                      title="漏斗"
                      extra={<Select allowClear style={{ width: 180 }} placeholder="漏斗 ID" value={funnelId} options={funnelOptions} onChange={setFunnelId} />}
                    >
                      {funnelId ? (
                        <Table size="small" rowKey={(r) => String(r.goalId || r.goal_id || r.stepOrder || r.step_order)} loading={goalsLoading} dataSource={funnel} columns={funnelColumns} pagination={false} />
                      ) : (
                        <Empty description="用相同的漏斗 ID 创建多个目标，即可生成转化漏斗" />
                      )}
                    </Card>
                </Space>
                </PlanGate>
              ),
            },
            {
              key: "insights",
              label: t("analytics.tab.insights"),
              children: (
                <PlanGate feature={t("analytics.gate.insights")} requiredPlan="pro_max" description={t("analytics.gate.insightsDesc")}>
                  <Space direction="vertical" style={{ width: "100%" }} size="middle">
                    <Card
                      size="small"
                      title={t("analytics.insights.title")}
                      extra={
                        <Button type="primary" loading={aiReportLoading} disabled={!siteId} onClick={fetchAIReport}>
                          {t("analytics.insights.generate")}
                        </Button>
                      }
                    >
                      <Paragraph type="secondary">{t("analytics.insights.desc")}</Paragraph>
                      {aiReport ? (
                        <Row gutter={[16, 16]}>
                          <Col xs={24} md={8}>
                            <Card size="small" type="inner" title={t("analytics.insights.summary")}>
                              {(aiReport.summary || []).length > 0 ? (
                                <ul style={{ paddingLeft: 18, margin: 0 }}>
                                  {(aiReport.summary || []).map((line, idx) => (
                                    <li key={idx}>{line}</li>
                                  ))}
                                </ul>
                              ) : (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("common.empty")} />
                              )}
                            </Card>
                          </Col>
                          <Col xs={24} md={8}>
                            <Card size="small" type="inner" title={t("analytics.insights.alerts")}>
                              {(aiReport.alerts || []).length > 0 ? (
                                <ul style={{ paddingLeft: 18, margin: 0 }}>
                                  {(aiReport.alerts || []).map((line, idx) => (
                                    <li key={idx}>{line}</li>
                                  ))}
                                </ul>
                              ) : (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("common.empty")} />
                              )}
                            </Card>
                          </Col>
                          <Col xs={24} md={8}>
                            <Card size="small" type="inner" title={t("analytics.insights.suggestions")}>
                              {(aiReport.suggestions || []).length > 0 ? (
                                <ul style={{ paddingLeft: 18, margin: 0 }}>
                                  {(aiReport.suggestions || []).map((line, idx) => (
                                    <li key={idx}>{line}</li>
                                  ))}
                                </ul>
                              ) : (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("common.empty")} />
                              )}
                            </Card>
                          </Col>
                        </Row>
                      ) : (
                        <Empty description={t("analytics.insights.notGenerated")} />
                      )}
                    </Card>
                    {/* 效果 -> 原因 -> 成本 三者互跳(11§6.6) */}
                    <Card size="small" title={t("analytics.insights.relatedTitle")}>
                      <Space wrap>
                        <Button onClick={() => navigate("/knowledge")}>{t("analytics.insights.toKnowledge")}</Button>
                        <Button onClick={() => navigate("/annotations/audit")}>{t("analytics.insights.toAudit")}</Button>
                        <Button onClick={() => navigate("/agents")}>{t("analytics.insights.toTrace")}</Button>
                        <Button onClick={() => navigate("/token-usage")}>{t("analytics.insights.toUsage")}</Button>
                      </Space>
                    </Card>
                  </Space>
                </PlanGate>
              ),
            },
            {
              key: "settings",
              label: "设置",
              children: (
                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={14}>
                    <Card size="small" title="隐私与过滤" loading={settingsLoading}>
                      <Form form={settingsForm} layout="vertical" initialValues={{ anonymizeIp: false }}>
                        <Form.Item name="anonymizeIp" label="IP 匿名化">
                          <Select options={[{ value: true, label: "启用" }, { value: false, label: "停用" }]} />
                        </Form.Item>
                        <Form.Item name="excludePathPrefix" label="排除路径前缀">
                          <Select mode="tags" tokenSeparators={[",", "\n"]} placeholder="/admin, /preview" />
                        </Form.Item>
                        <Form.Item name="excludeIps" label="排除 IP">
                          <Select mode="tags" tokenSeparators={[",", "\n"]} placeholder="127.0.0.1, 10.0.0.*" />
                        </Form.Item>
                        <Form.Item name="excludeUas" label="排除 User-Agent 关键词">
                          <Select mode="tags" tokenSeparators={[",", "\n"]} placeholder="bot, preview" />
                        </Form.Item>
                        <Form.Item name="excludeVisitorIds" label="排除访客 ID">
                          <Select mode="tags" tokenSeparators={[",", "\n"]} placeholder="visitor id" />
                        </Form.Item>
                        <Button type="primary" loading={settingsSaving} onClick={handleSaveSettings}>
                          保存设置
                        </Button>
                      </Form>
                    </Card>
                  </Col>
                  <Col xs={24} lg={10}>
                    <Card size="small" title="Do Not Track（请勿追踪）">
                      <Paragraph type="secondary">
                        统一 SDK 默认遵循浏览器的 Do Not Track 设置：开启时停止采集统计事件，但摘要 / 对话功能不受影响。
                      </Paragraph>
                    </Card>
                  </Col>
                </Row>
              ),
            },
            {
              key: "install",
              label: "接入代码",
              children: currentSite ? (
                <div>
                  <Paragraph type="secondary">
                    将以下代码插入网站所有页面的 <Text code>&lt;head&gt;</Text> 中;收到第一条访问事件后即接入成功。
                  </Paragraph>
                  <Paragraph copyable={{ text: installSnippet }}>
                    <Text code>{installSnippet}</Text>
                  </Paragraph>
                  <Paragraph type="secondary">
                    站点 Key:<Text code>{currentSite.site_key}</Text>
                  </Paragraph>
                </div>
              ) : (
                <Empty />
              ),
            },
          ]}
        />
      </Card>

      <CreateSiteModal
        open={createOpen}
        confirmLoading={creating}
        form={createForm}
        onOk={handleCreate}
        onCancel={() => setCreateOpen(false)}
      />
    </div>
  );
};

const CreateSiteModal: React.FC<{
  open: boolean;
  confirmLoading: boolean;
  form: any;
  onOk: () => void;
  onCancel: () => void;
}> = ({ open, confirmLoading, form, onOk, onCancel }) => (
  <Modal title="新建统计站点" open={open} confirmLoading={confirmLoading} onOk={onOk} onCancel={onCancel} destroyOnClose>
    <Form form={form} layout="vertical">
      <Form.Item name="siteName" label="站点名称" rules={[{ required: true, message: "请输入站点名称" }]}>
        <Input placeholder="例如:我的博客" maxLength={50} />
      </Form.Item>
      <Form.Item
        name="domain"
        label="站点域名"
        rules={[{ required: true, message: "请输入域名" }]}
        extra="用于来源归类与外链识别,例如 blog.example.com"
      >
        <Input placeholder="blog.example.com" maxLength={100} />
      </Form.Item>
    </Form>
  </Modal>
);

export default Analytics;
